// Окно «Сообщить о проблеме» (2026-09-12, карточка #20): текст + снимок экрана →
// POST /api/feedback → запись в базе и карточка в GitHub с меткой «тестеры».
// Одно и то же в Каскаде (/app/) и на сайте подрядчиков (/fakt/): кнопка внизу
// справа, окно в стиле общих диалогов (.dlg). Снимок вставляется Ctrl+V
// (после Win+Shift+S) или файлом; перед отправкой сжимается в браузере до
// ~650 КБ — nginx пропускает тело запроса до 1 МБ, а base64 добавляет треть.
//
// Просмотр (ZamechanieProsmotr): ссылка из карточки GitHub ведёт на
// /app/#zamechanie=N — после входа показываем текст и снимок; сам файл отдаёт
// /api/feedback/N/image только автору и администратору, с токеном в заголовке.
import { useEffect, useRef, useState } from 'react';
import { call, type Data } from './api';

type Otvet = Data<'/api/feedback', 'post'>;
type Zamechanie = Data<'/api/feedback/{fid}', 'get'>;

const MAX_STORONA = 1600;          // px, длинная сторона снимка
const PREDEL_BAYT = 650 * 1024;    // после сжатия

/** Картинка → data-URL JPEG не больше PREDEL_BAYT: уменьшаем сторону, потом качество. */
export async function szhatSnimok(src: Blob): Promise<string> {
  const url = URL.createObjectURL(src);
  try {
    const img = await new Promise<HTMLImageElement>((ok, net) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => net(new Error('картинка не прочиталась'));
      i.src = url;
    });
    let k = Math.min(1, MAX_STORONA / Math.max(img.naturalWidth, img.naturalHeight));
    for (let popytka = 0; popytka < 6; popytka++) {
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(img.naturalWidth * k));
      c.height = Math.max(1, Math.round(img.naturalHeight * k));
      const g = c.getContext('2d');
      if (!g) throw new Error('canvas недоступен');
      g.fillStyle = '#fff';
      g.fillRect(0, 0, c.width, c.height);   // прозрачное → белое
      g.drawImage(img, 0, 0, c.width, c.height);
      for (const q of [0.85, 0.75, 0.65]) {
        const d = c.toDataURL('image/jpeg', q);
        if (d.length * 0.75 <= PREDEL_BAYT) return d;
      }
      k *= 0.7;   // всё ещё велик — уменьшаем ещё
    }
    throw new Error('снимок не удалось ужать до нужного размера');
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function ZamechanieKnopka({ pid, sait, ekran }:
  { pid: number | null; sait: 'app' | 'fakt'; ekran?: string }) {
  const [otkryto, setOtkryto] = useState(false);
  return (
    <>
      <button className="zam-btn" type="button"
        title="Заметили ошибку или чего-то не хватает — напишите, мы получим это списком задач"
        onClick={() => setOtkryto(true)}>Сообщить о проблеме</button>
      {otkryto && <ZamechanieOkno pid={pid} sait={sait} ekran={ekran} onClose={() => setOtkryto(false)} />}
    </>
  );
}

function ZamechanieOkno({ pid, sait, ekran, onClose }:
  { pid: number | null; sait: 'app' | 'fakt'; ekran?: string; onClose: () => void }) {
  const [text, setText] = useState('');
  const [snimok, setSnimok] = useState<string | null>(null);   // data-URL
  const [oshibka, setOshibka] = useState('');
  const [busy, setBusy] = useState(false);
  const [gotovo, setGotovo] = useState<Otvet | null>(null);
  const ta = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => { ta.current?.focus(); }, []);

  async function vzyat(blob: Blob | null | undefined) {
    if (!blob) return;
    setOshibka('');
    try {
      setSnimok(await szhatSnimok(blob));
    } catch (e) {
      setOshibka(String((e as Error).message || e));
    }
  }
  function onPaste(e: React.ClipboardEvent) {
    const it = Array.from(e.clipboardData?.items || []).find(x => x.type.startsWith('image/'));
    if (it) { e.preventDefault(); void vzyat(it.getAsFile()); }
  }
  async function otpravit() {
    const t = text.trim();
    if (!t) { setOshibka('Напишите, что не так'); ta.current?.focus(); return; }
    setBusy(true);
    setOshibka('');
    const r = await call<Otvet>('post', '/api/feedback', {
      text: t, image: snimok, sait, ekran: ekran || null, project_id: pid,
      adres: typeof window !== 'undefined' ? window.location.href.slice(0, 300) : null,
    });
    setBusy(false);
    if (!r.ok) { setOshibka(r.error.message); return; }
    setGotovo(r.data);
  }

  return (
    <div className="dlg-fon" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="dlg zam-okno" role="dialog" aria-modal="true" onPaste={onPaste}
        onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); if (!busy) onClose(); } }}>
        {gotovo ? (
          <>
            <div className="dlg-text">Спасибо, замечание №{gotovo.id} записано</div>
            <div className="dlg-podpis">
              {gotovo.gh_url
                ? 'Оно попало в общий список задач команды.'
                : 'Оно сохранено у нас; в список задач команды уйдёт позже'
                  + (gotovo.gh_error ? ' (' + gotovo.gh_error + ')' : '') + '.'}
            </div>
            <div className="dlg-knopki"><button onClick={onClose}>Закрыть</button></div>
          </>
        ) : (
          <>
            <div className="dlg-text">Сообщить о проблеме</div>
            <div className="dlg-podpis">
              Что не так и что хотелось бы. Экран: {ekran || '—'}.
              Снимок экрана: нажмите Win+Shift+S, выделите область и вставьте сюда Ctrl+V, либо прикрепите файл.
            </div>
            <textarea ref={ta} className="zam-text" value={text} maxLength={4000} rows={5}
              placeholder="Например: на экране «Этажи» после «+ доп. этаж» номер не сохраняется…"
              onChange={e => setText(e.target.value)} disabled={busy} />
            <div className="zam-snimok">
              {snimok ? (
                <>
                  <img src={snimok} alt="снимок экрана" />
                  <a className="zam-ubrat" onClick={() => setSnimok(null)}>убрать снимок</a>
                </>
              ) : (
                <>
                  <button type="button" className="sec" disabled={busy}
                    onClick={() => fileRef.current?.click()}>Прикрепить снимок</button>
                  <span className="muted">или Ctrl+V прямо сюда</span>
                  <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={e => { void vzyat(e.target.files?.[0]); e.target.value = ''; }} />
                </>
              )}
            </div>
            {oshibka && <div className="pw-err">{oshibka}</div>}
            <div className="dlg-knopki">
              <button className="sec" disabled={busy} onClick={onClose}>Отмена</button>
              <button disabled={busy} onClick={() => void otpravit()}>{busy ? 'Отправляю…' : 'Отправить'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function nomerIzAdresa(): number | null {
  if (typeof window === 'undefined') return null;
  const m = /[#&]zamechanie=(\d+)/.exec(window.location.hash);
  return m ? parseInt(m[1], 10) : null;
}

/** Просмотр замечания по ссылке из карточки GitHub: /app/#zamechanie=N */
export function ZamechanieProsmotr() {
  const [nomer, setNomer] = useState<number | null>(nomerIzAdresa);
  const [z, setZ] = useState<Zamechanie | null>(null);
  const [img, setImg] = useState<string | null>(null);
  const [oshibka, setOshibka] = useState('');
  useEffect(() => {
    const f = () => setNomer(nomerIzAdresa());
    window.addEventListener('hashchange', f);
    return () => window.removeEventListener('hashchange', f);
  }, []);
  useEffect(() => {
    if (!nomer) { setZ(null); setImg(null); setOshibka(''); return; }
    let zhiv = true;
    void (async () => {
      const r = await call<Zamechanie>('get', '/api/feedback/' + nomer);
      if (!zhiv) return;
      if (!r.ok) { setOshibka(r.error.message); return; }
      setZ(r.data);
      if (r.data.snimok) {
        let t: string | null = null;
        try { t = localStorage.getItem('plot_t'); } catch { /* приватный режим */ }
        const resp = await fetch('/api/feedback/' + nomer + '/image',
          { headers: t ? { Authorization: 'Bearer ' + t } : {} });
        if (zhiv && resp.ok) setImg(URL.createObjectURL(await resp.blob()));
      }
    })();
    return () => { zhiv = false; };
  }, [nomer]);
  useEffect(() => () => { if (img) URL.revokeObjectURL(img); }, [img]);
  if (!nomer) return null;
  const zakryt = () => { window.location.hash = ''; };
  return (
    <div className="dlg-fon" onMouseDown={e => { if (e.target === e.currentTarget) zakryt(); }}>
      <div className="dlg zam-okno zam-prosmotr" role="dialog" aria-modal="true">
        <div className="dlg-text">Замечание №{nomer}</div>
        {oshibka && <div className="pw-err">{oshibka}</div>}
        {z && (
          <>
            <div className="dlg-podpis">
              {z.kto} · {new Date(z.kogda).toLocaleString('ru-RU')}
              {' · '}{z.sait === 'app' ? 'Каскад' : 'Шахматка подрядчиков'}
              {z.proekt ? ' · ' + z.proekt : ''}{z.ekran ? ' · ' + z.ekran : ''}
              {z.gh_url && <> · <a href={z.gh_url} target="_blank" rel="noopener noreferrer">карточка в GitHub</a></>}
            </div>
            <div className="zam-tekst">{z.text}</div>
            {z.snimok && (img
              ? <img className="zam-img" src={img} alt="снимок экрана" />
              : <div className="muted">Загружаю снимок…</div>)}
          </>
        )}
        <div className="dlg-knopki"><button onClick={zakryt}>Закрыть</button></div>
      </div>
    </div>
  );
}
